import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Zap,
  Flame,
  ChevronRight,
  TrendingUp,
  AlertTriangle,
  Loader2,
  Target,
} from "lucide-react";
import { getPriorityQueue, PriorityFinding } from "../../api/client";
import SeverityBadge, { Severity } from "./SeverityBadge";

interface PriorityQueuePanelProps {
  repoId: string;
  onFindingClick: (finding: PriorityFinding) => void;
  limit?: number;
}

function getPriorityColor(score: number): string {
  if (score >= 80) return "text-critical";
  if (score >= 60) return "text-high";
  if (score >= 40) return "text-medium";
  return "text-low";
}

function getPriorityBg(score: number): string {
  if (score >= 80) return "bg-critical/10 border-critical/30";
  if (score >= 60) return "bg-high/10 border-high/30";
  if (score >= 40) return "bg-medium/10 border-medium/30";
  return "bg-low/10 border-low/30";
}

export default function PriorityQueuePanel({
  repoId,
  onFindingClick,
  limit = 10,
}: PriorityQueuePanelProps) {
  const priorityQuery = useQuery({
    queryKey: ["priorityQueue", repoId, limit],
    queryFn: () => getPriorityQueue(repoId, { limit }),
    enabled: Boolean(repoId),
    refetchInterval: 60000, // Refresh every minute
  });

  const findings = priorityQuery.data || [];

  if (priorityQuery.isLoading) {
    return (
      <div className="card p-6">
        <div className="flex items-center gap-2 text-sm text-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading priority queue...
        </div>
      </div>
    );
  }

  if (findings.length === 0) {
    return null; // Don't show if no priority findings
  }

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-divider bg-surface2/30 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-brand/15">
            <Target className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text">Priority Queue</h3>
            <p className="text-xs text-muted">Top vulnerabilities to fix first</p>
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted">
          <TrendingUp className="h-3.5 w-3.5" />
          Sorted by risk
        </div>
      </div>

      {/* Priority list */}
      <div className="divide-y divide-divider">
        {findings.map((finding, index) => (
          <button
            key={finding.id}
            onClick={() => onFindingClick(finding)}
            className="w-full px-5 py-4 hover:bg-surface2/50 transition-colors text-left group"
          >
            <div className="flex items-start gap-4">
              {/* Rank */}
              <div
                className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold border ${getPriorityBg(
                  finding.priority_score
                )} ${getPriorityColor(finding.priority_score)}`}
              >
                {index + 1}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {/* KEV indicator */}
                  {finding.is_kev && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-critical/20 text-critical rounded animate-pulse">
                      <Flame className="h-3 w-3" />
                      KEV
                    </span>
                  )}

                  {/* Priority score */}
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded ${getPriorityBg(
                      finding.priority_score
                    )} ${getPriorityColor(finding.priority_score)}`}
                  >
                    <Zap className="h-3 w-3" />
                    P{finding.priority_score}
                  </span>

                  <SeverityBadge severity={finding.severity as Severity} size="sm" />

                  {/* CVE */}
                  {finding.cve_id && (
                    <span className="text-[10px] font-mono text-critical bg-critical/10 px-1.5 py-0.5 rounded">
                      {finding.cve_id}
                    </span>
                  )}
                </div>

                {/* Title */}
                <div className="text-sm font-medium text-text group-hover:text-brand transition-colors truncate">
                  {finding.title}
                </div>

                {/* File path */}
                {finding.file_path && (
                  <div className="text-xs text-muted font-mono truncate mt-1">
                    {finding.file_path}
                    {finding.start_line && `:${finding.start_line}`}
                  </div>
                )}

                {/* Risk factors */}
                <div className="flex items-center gap-3 mt-2 text-xs text-muted">
                  {finding.epss_score !== undefined && finding.epss_score > 0 && (
                    <span>EPSS: {(finding.epss_score * 100).toFixed(1)}%</span>
                  )}
                  {finding.cvss_score !== undefined && finding.cvss_score > 0 && (
                    <span>CVSS: {finding.cvss_score.toFixed(1)}</span>
                  )}
                  {finding.risk_score !== undefined && finding.risk_score > 0 && (
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Risk: {finding.risk_score}
                    </span>
                  )}
                </div>
              </div>

              {/* Arrow */}
              <ChevronRight className="h-5 w-5 text-muted opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
            </div>
          </button>
        ))}
      </div>

      {/* Footer */}
      {findings.length >= limit && (
        <div className="px-5 py-3 border-t border-divider bg-surface2/30 text-center">
          <span className="text-xs text-muted">
            Showing top {limit} • Use filters to see more
          </span>
        </div>
      )}
    </div>
  );
}
